import React, { useCallback, useState, useEffect } from "react";
import {
  Image,
  Form,
  Checkbox,
  Button,
  Icon,
  Dropdown,
  Card,
} from "semantic-ui-react";
import { useFormik } from "formik";
import { initialValues, validationSchema } from "./TeacherForm.form";
import { TeacherAPI, InstitutionApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import "./TeacherForm.scss";

const teacherApi = new TeacherAPI();
const institutionController = new InstitutionApi();

export function TeacherForm({ onClose, onReload, teacher }) {
  const { accessToken, user } = useAuth();
  const { t } = useTranslation(["teachers", "common"]);
  const institutionId = user.user.institutions[0];

  const [institution, setInstitution] = useState(null);
  const [classroleList, setClassroleList] = useState(teacher?.classrole || []);
  const [selectedClassnumber, setSelectedClassnumber] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  useEffect(() => {
    institutionController
      .getInstitutionsbyInstitution(accessToken, institutionId, true)
      .then((response) => setInstitution(response.institution))
      .catch(console.error);
  }, [accessToken, institutionId]);

  const classNumberOptions = (institution?.classnumber || []).map((cn) => ({
    key: cn,
    value: cn,
    text: cn,
  }));

  const roleOptions = [
    { key: "docente", value: "Docente", text: "Docente" },
    { key: "tutor", value: "Tutor/a", text: "Tutor/a" },
    {
      key: "jefeestudio",
      value: "Jefe/a de Estudio",
      text: "Jefe/a de Estudio",
    },
    { key: "director", value: "Director/a", text: "Director/a" },
  ];

  const formik = useFormik({
    initialValues: initialValues(teacher),
    validationSchema: validationSchema(teacher, t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (classroleList.length === 0) {
        alert(t("teachers:form.needRole"));
        return;
      }
      try {
        const formData = new FormData();
        formData.append("firstname", formValue.firstname);
        formData.append("lastname", formValue.lastname);
        formData.append("email", formValue.email);
        if (formValue.password) formData.append("password", formValue.password);
        formData.append("active", formValue.active);
        formData.append("classrole", JSON.stringify(classroleList));
        if (formValue.fileAvatar)
          formData.append("avatar", formValue.fileAvatar);

        if (!teacher) {
          await teacherApi.createTeacher(accessToken, formData, institutionId);
        } else {
          await teacherApi.updateTeacher(accessToken, teacher._id, formData);
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        formik.setFieldValue("avatar", URL.createObjectURL(file));
        formik.setFieldValue("fileAvatar", file);
      }
    },
    [formik],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop,
    multiple: false,
  });

  const handleAddClassrole = () => {
    if (!selectedClassnumber || !selectedRole) return;
    setClassroleList([
      ...classroleList,
      { classnumber: selectedClassnumber, role: selectedRole },
    ]);
    setSelectedClassnumber("");
    setSelectedRole("");
  };

  const handleDeleteClassrole = (index) =>
    setClassroleList(classroleList.filter((_, i) => i !== index));

  return (
    <Form className="teacher-form" onSubmit={formik.handleSubmit}>
      <div {...getRootProps()} className="avatar-upload">
        <input {...getInputProps()} />
        <Image
          avatar
          size="small"
          src={formik.values.avatar || image.noAvatar}
        />
      </div>

      <Form.Input
        name="firstname"
        placeholder={t("teachers:form.firstnamePlaceholder")}
        {...formik.getFieldProps("firstname")}
      />
      <Form.Input
        name="lastname"
        placeholder={t("teachers:form.lastnamePlaceholder")}
        {...formik.getFieldProps("lastname")}
      />
      <Form.Input
        name="email"
        placeholder={t("teachers:form.emailPlaceholder")}
        {...formik.getFieldProps("email")}
      />
      <Form.Input
        type="password"
        name="password"
        placeholder={t("teachers:form.passwordPlaceholder")}
        {...formik.getFieldProps("password")}
      />

      <Checkbox
        toggle
        label={t("teachers:form.activeLabel")}
        checked={formik.values.active}
        onChange={(_, d) => formik.setFieldValue("active", d.checked)}
      />

      <Dropdown
        fluid
        selection
        placeholder={t("teachers:form.classPlaceholder")}
        options={classNumberOptions}
        value={selectedClassnumber}
        onChange={(_, d) => setSelectedClassnumber(d.value)}
      />
      <Dropdown
        fluid
        selection
        placeholder={t("teachers:form.rolePlaceholder")}
        options={roleOptions}
        value={selectedRole}
        onChange={(_, d) => setSelectedRole(d.value)}
      />

      <Button type="button" primary onClick={handleAddClassrole}>
        <Icon name="plus" />
        {t("teachers:form.addRoleButton")}
      </Button>

      <Card.Group itemsPerRow={3}>
        {classroleList.map((item, index) => (
          <Card key={index}>
            <Card.Content>
              <Card.Header>{item.classnumber}</Card.Header>
              <Card.Meta>{item.role}</Card.Meta>
              <Button
                icon="trash"
                color="red"
                size="mini"
                floated="right"
                onClick={() => handleDeleteClassrole(index)}
              />
            </Card.Content>
          </Card>
        ))}
      </Card.Group>

      <Button type="submit" primary fluid disabled={classroleList.length === 0}>
        {teacher
          ? t("teachers:form.updateButton")
          : t("teachers:form.createButton")}
      </Button>
    </Form>
  );
}
